import { HttpClientPort } from '../../domain/ports/HttpClientPort';

interface ErrorResponseBody {
  message?: string;
}

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL?.trim() || null;

export class HttpRequestError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'HttpRequestError';
  }
}

export class FetchHttpClientAdapter implements HttpClientPort {
  async get<TResponse>(url: string, init?: RequestInit): Promise<TResponse> {
    return this.request<TResponse>(url, {
      ...init,
      method: 'GET',
    });
  }

  async post<TRequest, TResponse>(
    url: string,
    body: TRequest,
    init?: RequestInit,
  ): Promise<TResponse> {
    return this.request<TResponse>(url, {
      ...init,
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  private async request<TResponse>(url: string, init: RequestInit): Promise<TResponse> {
    const response = await fetch(this.resolveUrl(url), {
      ...init,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...init.headers,
      },
    });

    if (!response.ok) {
      throw new HttpRequestError(await this.buildErrorMessage(response), response.status);
    }

    return (await response.json()) as TResponse;
  }

  private resolveUrl(url: string): string {
    if (/^https?:\/\//.test(url) || apiBaseUrl === null) {
      return url;
    }

    return new URL(url, apiBaseUrl).toString();
  }

  private async buildErrorMessage(response: Response): Promise<string> {
    try {
      const body = (await response.json()) as ErrorResponseBody;

      if (typeof body.message === 'string' && body.message.trim() !== '') {
        return body.message;
      }
    } catch {
      // Ignore invalid JSON and fallback to HTTP status.
    }

    return `Request failed with status ${response.status}.`;
  }
}
